import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";

export default function LoanPaymentCalculator() {
  const [principal, setPrincipal] = useState("");
  const [rate, setRate] = useState("");
  const [term, setTerm] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const P = parseFloat(principal);
    const annualRate = parseFloat(rate);
    const months = parseFloat(term);

    if (isNaN(P) || isNaN(annualRate) || isNaN(months) || P <= 0 || annualRate <= 0 || months <= 0) {
      toast.error("Please enter valid positive values");
      return;
    }

    const monthlyRate = annualRate / 100 / 12;
    const payment = P * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);

    setResult(payment);
    toast.success("Loan payment calculated!");
  };

  const reset = () => {
    setPrincipal("");
    setRate("");
    setTerm("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Loan Payment Calculator"
      calculatorPath="/calculator/loan-payment"
      category="finance"
      subcategory="Loans"
    >
      <Card>
        <CardHeader>
          <CardTitle>Loan Payment Calculator</CardTitle>
          <CardDescription>
            Calculate monthly loan payments with principal and interest
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="principal">Loan Amount ($)</Label>
            <Input
              id="principal"
              type="number"
              placeholder="20000"
              value={principal}
              onChange={(e) => setPrincipal(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rate">Annual Interest Rate (%)</Label>
            <Input
              id="rate"
              type="number"
              placeholder="6"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="term">Loan Term (months)</Label>
            <Input
              id="term"
              type="number"
              placeholder="60"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={calculate}>Calculate</Button>
            <Button onClick={reset} variant="outline">Reset</Button>
          </div>
          {result !== null && (
            <>
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <p className="text-lg break-all overflow-wrap-anywhere">
                    <strong>Monthly Payment:</strong> ${result.toFixed(2)}
                  </p>
                  <CopyResultButton value={result.toFixed(2)} />
                </div>
              </div>

              <div className="mt-8">
                <CalculatorSuperCalcCTA config={calculatorCTAs["loan-payment"]} trigger={result} />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  );
}
